import { ref, computed } from 'vue'

import { defineStore } from 'pinia'
import { event } from 'vue-gtag'


import { useNotificationStore } from '@/stores/helpers/notification.js'
import { useAuthStore } from '@/stores/retailer/auth.js'
import UserService from '@/services/retailer/UserService.js'
import i18n from '@/i18n/i18n.js'

import { errorMessage } from '@/utilities/helpers.js'

import { USER_ERROR_MESSAGES } from '@/constants/errorMessages.js'

const { t } = i18n.global

export const useUserStore = defineStore('retailerUser', () => {
  const notificationStore = useNotificationStore()

  const users = ref([])
  const user = ref({})
  const meta = ref(null)

  const getUser = computed(() => {
    return user.value
  })

  const getUsers = computed(() => {
    return users.value
  })

  const getUserById = computed(() => id => {
    return users.value ? users.value.find(u => u.id === id) : false
  })

  const editAccountUser = userData => {
    return new Promise((resolve, reject) => {
      const authStore = useAuthStore()
      const retailerUser = authStore.user
      UserService.editUser(retailerUser.id, userData)
        .then(response => {
          user.value = response.data
          const notification = {
            type: 'success',
            message: t('store.shared.data_updated')
          }

          notificationStore.add(notification)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: `${t('store.shared.data_update_error')}: ${errorMessage(err)}`
          }
          notificationStore.add(notification)
          reject(err)
        })
    })
  }

  const editAccountPassword = password => {
    return new Promise((resolve, reject) => {
      const authStore = useAuthStore()
      const retailerUser = authStore.user
      UserService.editPassword(retailerUser.id, password)
        .then(response => {
          const notification = {
            type: 'success',
            message: t('store.shared.updated', { element: t('views.retailer.account.password') })
          }

          notificationStore.add(notification)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          notificationStore.add(notification)
          reject(err)
        })
    })
  }

  const fetchUser = async id => {
    if (user.value.id && id === user.value.id) {
      return user.value
    }
    return new Promise((resolve, reject) => {
      UserService.getUser(id)
        .then(response => {
          user.value = response.data
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: `${USER_ERROR_MESSAGES.getUserError}: ${errorMessage(err)}`
          }
          notificationStore.add(notification)
          reject(err)
        })
    })
  }

  const fetchUsers = query => {
    return new Promise((resolve, reject) => {
      UserService.getUsers(query)
        .then(response => {
          users.value = response.data
          meta.value = response.meta
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          notificationStore.add(notification)
          reject(err)
        })
    })
  }

  const createUser = userData => {
    return new Promise((resolve, reject) => {
      UserService.createUser(userData)
        .then(response => {
          users.value.push(response.data)
          const notification = {
            type: 'success',
            message: t('store.shared.created', { element: t('views.retailer.users.index.new_user') })
          }
          notificationStore.add(notification)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          notificationStore.add(notification)
          reject(err)
        })
    })
  }

  const editUser = params => {
    return new Promise((resolve, reject) => {
      UserService.editUser(params.id, params.user)
        .then(response => {
          user.value = response.data
          const notification = {
            type: 'success',
            message: t('store.shared.data_updated')
          }

          notificationStore.add(notification)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          notificationStore.add(notification)
          reject(err)
        })
    })
  }

  const deleteUser = params => {
    return new Promise((resolve, reject) => {
      UserService.deleteUser(params.id)
        .then(response => {
          event('user_actions', {
            event_action: 'user_deleted'
          })
          users.value = response.data
          meta.value = response.meta
          const notification = {
            type: 'success',
            message: t('store.shared.deleted', { element: t('views.admin.applications.index.filters.applicant_id') })
          }
          notificationStore.add(notification)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: USER_ERROR_MESSAGES.deleteUserError + errorMessage(err)
          }
          event('error', {
            request_type: 'retailer_delete_user',
            error_message: notification
          })
          notificationStore.add(notification)
          reject(err)
        })
    })
  }

  const validatePassword = userData => {
    return new Promise((resolve, reject) => {
      UserService.validatePassword(userData)
        .then(response => {
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          notificationStore.add(notification)
          reject(err)
        })
    })
  }

  const markNotificationsAsRead = userId => {
    return new Promise((resolve, reject) => {
      UserService.markAsReadNotifications(userId)
        .then(response => {
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          notificationStore.add(notification)
          reject(err)
        })
    })
  }

  return {
    users,
    user,
    meta,
    getUser,
    getUsers,
    getUserById,
    editAccountUser,
    editAccountPassword,
    fetchUser,
    fetchUsers,
    createUser,
    editUser,
    deleteUser,
    validatePassword,
    markNotificationsAsRead
  }
})
